import React, { useEffect, useState } from "react";
import { Gift, Loader2, Sparkles, Heart } from "lucide-react";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Shows a "you've been gifted a pack!" celebration for any unclaimed gifts sent to the user.
export default function GiftReveal({ onClaimed }) {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/marketplace/gifts/unseen").then((r) => {
      if (alive && Array.isArray(r.data) && r.data.length) setQueue(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const current = queue[0];
  useEffect(() => {
    if (current) {
      const t = setTimeout(() => {
        confetti({ particleCount: 140, spread: 78, origin: { y: 0.35 }, zIndex: 9999 });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [current]);

  if (!current) return null;

  const claim = async () => {
    setBusy(true);
    try { await api.post(`/marketplace/gifts/${current.id}/seen`); } catch {}
    setBusy(false);
    setQueue((q) => q.slice(1));
    onClaimed && onClaimed();
  };
  const thank = async () => {
    setBusy(true);
    try { await api.post(`/marketplace/gifts/${current.id}/thanks`); toast.success(`Thanks sent to ${current.from_name}! 💛`); } catch {}
    setBusy(false);
    setQueue((q) => q.slice(1));
    onClaimed && onClaimed();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) claim(); }}>
      <DialogContent data-testid="gift-reveal" className="overflow-hidden border-fuchsia-500/40">
        <div className="text-center py-2">
          <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-[#3b9eff] text-white shadow-lg shadow-fuchsia-500/30 animate-bounce">
            <Gift className="w-8 h-8" />
          </div>
          <h2 className="font-display text-2xl font-extrabold mt-4 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" /> You've been gifted a pack!
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1" data-testid="gift-reveal-from">
            <b className="text-[hsl(var(--foreground))]">{current.from_name}</b> sent you{" "}
            <b className="text-[hsl(var(--foreground))]">{current.seller_name}'s</b> pack — {current.item_count} item{current.item_count === 1 ? "" : "s"} added to your library.
          </p>

          {current.note && (
            <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] px-4 py-3 text-sm italic" data-testid="gift-reveal-note">
              “{current.note}”
            </div>
          )}

          {current.items?.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="gift-reveal-items">
              {current.items.map((it, i) => (
                <div key={i} title={it.title}
                  className="w-14 h-14 rounded-xl grid place-items-center text-2xl bg-gradient-to-br from-fuchsia-500/15 to-[#3b9eff]/15 border border-[hsl(var(--border))] overflow-hidden">
                  {it.preview_image
                    ? <img src={it.preview_image.startsWith("http") ? it.preview_image : `${BACKEND}${it.preview_image}`} alt={it.title} className="w-full h-full object-cover" />
                    : <span>{it.preview_emoji}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 space-y-2">
            <Button onClick={thank} disabled={busy} data-testid="gift-reveal-thanks"
              className="w-full rounded-full bg-gradient-to-r from-amber-400 to-pink-500 text-black font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4 fill-black" />} Say thanks to {current.from_name} 💛
            </Button>
            <Button onClick={claim} disabled={busy} variant="ghost" data-testid="gift-reveal-claim"
              className="w-full rounded-full font-semibold text-[hsl(var(--muted-foreground))]">
              Just add to my library
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
